/* =====================================================
 * Frontend permissions config.
 *
 * The backend is the source of truth — these constants only drive the
 * sidebar's local visibility filter so users don't see menu items they
 * can't open anyway.
 *
 * MODULES mirrors backend/config/permissions.js. Each row has the
 * permission key the API gate uses + the exact sidebar label shown to
 * the user. The sidebar maps from label → key via LABEL_TO_KEY.
 *
 * Effective module list comes from /api/auth/me as { user.modules: [keys] }.
 * Pass that array to canSee(modulesArray, label). For backwards compat we
 * still expose canSee(role, label) using LEAF_PERMISSIONS so any code that
 * passes a role string keeps working.
 * ===================================================== */

export const MODULES = [
  { key: "dashboard",        label: "Dashboard" },
  { key: "accounts",         label: "ACCOUNTS" },
  { key: "products",         label: "PRODUCTS" },
  { key: "purchaseInvoice",      label: "PURCHASE INVOICE" },
  { key: "purchaseInvoicePend",  label: "PURCHASE INVOICE PENDING" },
  { key: "brokerPayments",       label: "BROKER PAYMENTS" },
  { key: "saleInvoicePend",      label: "SALE INVOICE PENDING" },
  { key: "purchaseOrders",   label: "PURCHASE ORDERS" },
  { key: "commissions",      label: "COMMISSIONS" },
  { key: "intakes",          label: "PENDING INTAKES" },
  { key: "saleInvoice",      label: "SALE INVOICE" },
  { key: "saleOrders",       label: "SALE ORDERS" },
  { key: "booking",          label: "BOOKING #" },
  { key: "container",        label: "CONTAINER FORM" },
  { key: "pickup",           label: "PICKUP SCHEDULED" },
  { key: "companyDocuments", label: "COMPANY DOCUMENTS" },
  { key: "employeeLoans",    label: "EMPLOYEE LOANS" },
  { key: "truckingInvoices", label: "TRUCKING INVOICES" },
  { key: "freight",          label: "FREIGHT LANE" },
  { key: "dailyCash",        label: "DAILY CASH" },
  { key: "regrade",          label: "REGRADE MATERIALS" },
  { key: "packingList",      label: "PACKING LIST" },
  { key: "finishedGoods",    label: "FINISHED GOODS" },
  { key: "wireChopping",     label: "WIRE CHOPPING" },
  { key: "wireChopping",     label: "RECOVERY ANALYTICS" },
  { key: "wireLibrary",      label: "WIRE LIBRARY" },
  { key: "identifyWire",     label: "IDENTIFY WIRE" },
  { key: "pdfReports",       label: "STANDARD REPORTS" },
  { key: "freightReports",   label: "FREIGHT REPORTS" },
  { key: "generalLedger",    label: "GENERAL LEDGER" },
  { key: "accounting",       label: "CHART OF ACCOUNTS" },
  { key: "accounting",       label: "JOURNAL ENTRY" },
  { key: "accounting",       label: "PROFIT & LOSS" },
  { key: "accounting",       label: "BALANCE SHEET" },
  { key: "accounting",       label: "TRIAL BALANCE" },
  { key: "accounting",       label: "INVENTORY VALUATION" },
  { key: "accounting",       label: "INVENTORY AGING" },
  { key: "accounting",       label: "CONTAINER PROFIT" },
  { key: "accounting",       label: "AR / AP AGING" },
  { key: "accounting",       label: "GL ACCOUNT DETAIL" },
  { key: "accounting",       label: "CASH FLOW" },
  { key: "accounting",       label: "COMPARATIVE P&L" },
  { key: "accounting",       label: "CUSTOMER STATEMENT" },
  { key: "accounting",       label: "BANK RECONCILIATION" },
  { key: "quickbooks",       label: "QUICKBOOKS" },
  { key: "prices",           label: "PRICE DASHBOARD" },
  { key: "prices",           label: "PRICE ALERTS" },
  { key: "priceMappings",    label: "PRICE MAPPINGS" },
  { key: "priceMappings",    label: "MARKET PRICE LINK" },
  { key: "priceMappings",    label: "GLOMETS PRICE LIST" },
  { key: "userManagement",   label: "USERS" },
  { key: "employeePanel",    label: "EMPLOYEE PANEL" },
];

export const LABEL_TO_KEY = MODULES.reduce((acc, m) => {
  acc[m.label] = m.key;
  return acc;
}, {});

/* canSee(modulesOrRole, label)
 *   - If first arg is an array → treat as effective module keys; admin sidebar.
 *   - If first arg is a string → legacy role-based fallback (compat shim).
 *
 * Defaults to DENY for unknown labels — safer than the previous default of
 * allow, so a sidebar item we forgot to register here can't accidentally
 * leak to users who shouldn't see it.
 */
export function canSee(modulesOrRole, label) {
  const key = LABEL_TO_KEY[label];

  if (Array.isArray(modulesOrRole)) {
    if (!key) return false;                       // unknown leaf -> deny
    return modulesOrRole.includes(key);
  }
  // Legacy role fallback
  const allowedByRole = LEAF_PERMISSIONS[label];
  if (!allowedByRole) return false;
  return allowedByRole.includes(modulesOrRole);
}

/* ----- Legacy role-default visibility (used only as fallback) ----- */
const ALL = ["admin", "manager", "accountant", "cashier", "yard", "sales", "viewer", "employee"];
const ANY = (...names) => Array.from(new Set([...names, "admin", "manager", "employee"]));

export const LEAF_PERMISSIONS = {
  "Dashboard":           ALL,
  "PENDING INTAKES":     ANY("yard", "viewer"),
  "PURCHASE INVOICE":    ANY("accountant", "yard", "viewer"),
  "PURCHASE ORDERS":     ANY("accountant", "yard", "viewer"),
  "COMMISSIONS":         ANY("accountant"),
  "SALE INVOICE":        ANY("accountant", "sales", "yard", "viewer"),
  "SALE ORDERS":         ANY("accountant", "sales", "viewer"),
  "BOOKING #":           ANY("sales", "viewer"),
  "CONTAINER FORM":      ANY("sales", "viewer"),
  "PICKUP SCHEDULED":    ANY("sales", "viewer"),
  "COMPANY DOCUMENTS":   ANY("accountant", "sales", "viewer"),
  "EMPLOYEE LOANS":      ANY("accountant", "viewer"),
  "TRUCKING INVOICES":   ANY("accountant", "viewer"),
  "FREIGHT LANE":        ANY("sales", "viewer"),
  "ACCOUNTS":            ANY("accountant", "sales", "viewer"),
  "PRODUCTS":            ANY("accountant", "yard", "viewer"),
  "DAILY CASH":          ANY("accountant", "cashier", "viewer"),
  "REGRADE MATERIALS":   ANY("yard", "viewer"),
  "FINISHED GOODS":      ANY("yard", "viewer"),
  "WIRE CHOPPING":       ANY("yard"),
  "RECOVERY ANALYTICS":  ANY("yard"),
  "PACKING LIST":        ANY("yard", "sales", "viewer"),
  "WIRE LIBRARY":        ANY("yard", "viewer"),
  "IDENTIFY WIRE":       ANY("yard", "viewer"),
  "STANDARD REPORTS":    ANY("accountant", "viewer"),
  "GENERAL LEDGER":      ANY("accountant", "viewer"),
  "CHART OF ACCOUNTS":   ANY("accountant", "viewer"),
  "JOURNAL ENTRY":       ANY("accountant", "viewer"),
  "PROFIT & LOSS":       ANY("accountant", "viewer"),
  "BALANCE SHEET":       ANY("accountant", "viewer"),
  "TRIAL BALANCE":       ANY("accountant", "viewer"),
  "INVENTORY VALUATION": ANY("accountant", "viewer"),
  "INVENTORY AGING":     ANY("accountant", "viewer"),
  "CONTAINER PROFIT":    ANY("accountant", "viewer"),
  "AR / AP AGING":       ANY("accountant", "viewer"),
  "GL ACCOUNT DETAIL":   ANY("accountant", "viewer"),
  "CASH FLOW":           ANY("accountant", "viewer"),
  "COMPARATIVE P&L":     ANY("accountant", "viewer"),
  "CUSTOMER STATEMENT":  ANY("accountant", "viewer"),
  "BANK RECONCILIATION": ANY("accountant", "viewer"),
  "QUICKBOOKS":          ANY("accountant"),
  "PRICE DASHBOARD":     ANY("accountant", "sales", "yard", "viewer"),
  "PRICE ALERTS":        ANY("accountant", "sales", "yard", "viewer"),
  "PRICE MAPPINGS":      ANY("accountant"),
  "MARKET PRICE LINK":   ANY("accountant"),
  "GLOMETS PRICE LIST":  ANY("accountant", "sales", "yard"),
  "USERS":               ["admin"],
};
